/* eslint-disable i18next/no-literal-string */
import Button from '@ingka/button';
import type { FormFieldFieldHelper } from '@ingka/form-field';
import FormField from '@ingka/form-field';
import InlineMessage from '@ingka/inline-message';
import {
  DownloadSwipeQuestions,
  UploadSwipeQuestions,
} from '@lotta/self-service/lottery-fe/ui-common';
import { useCallback, useEffect, useState } from 'react';
import type {
  Control,
  FieldError,
  FieldValues,
  Path,
  UseFormClearErrors,
  UseFormSetError,
} from 'react-hook-form';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormFieldChildren } from '../../components/controlled-year-month-dropdown/FormFieldChildren';
import styles from '../../LotteryForm.module.scss';
import { shouldValidate } from '../../utils';
import type { QuestionsErrorFormatLimitsType } from '../../utils/error';
import { getLimit } from '../../utils/error';
import { swipeQuestionsTemplateData } from './questions-template-data';
import { UploadQuestionsErrorTheatre } from './upload-questions-error/UploadQuestionsErrorTheatre';

export type ControlledInputSwipeQuestionsProps<TFormData extends FieldValues = FieldValues> = {
  control: Control<TFormData>;
  name: Path<TFormData>;
  fieldHelper?: FormFieldFieldHelper;
  language: string;
  setError: UseFormSetError<TFormData>;
  clearErrors: UseFormClearErrors<TFormData>;
  requiredNumberOfQuestions: number;
  limits: QuestionsErrorFormatLimitsType;
};

export const ControlledInputSwipeQuestions = <TFormData extends FieldValues = FieldValues>({
  control,
  name,
  fieldHelper,
  language,
  setError,
  clearErrors,
  requiredNumberOfQuestions,
  limits,
}: ControlledInputSwipeQuestionsProps<TFormData>) => {
  const { t } = useTranslation();
  const questions = useWatch({ control, name });
  const [questionsFileName, setQuestionsFileName] = useState<string>(
    `${t('swipe-questions').toLowerCase()}.xlsx`
  );

  const handleError = useCallback(
    (error: FieldError) => {
      setError(name, error);
    },
    [name, setError]
  );

  useEffect(() => {
    if (questions) {
      if (!Array.isArray(questions) || questions.length < requiredNumberOfQuestions) {
        setError(name, { type: 'custom', message: 'swipe-upload-questions-error-minimum' });
      } else {
        clearErrors(name);
      }
    }
  }, [clearErrors, name, questions, requiredNumberOfQuestions, setError]);

  const [showErrors, setShowErrors] = useState(false);

  const transformErrorMessage = (error: FieldError | undefined) => {
    let errorMessage: string | undefined = undefined;

    if (!error) {
      return undefined;
    }

    if ((error?.type === 'custom' || error?.type === 'errorMessage') && error?.message) {
      errorMessage =
        error?.message === 'swipe-upload-questions-error-message-items-max-number'
          ? t(error?.message, getLimit(error?.message, limits))
          : t(error?.message);
    } else if (Array.isArray(error)) {
      errorMessage = t('swipe-upload-questions-format-error-text');
    }
    return errorMessage;
  };

  const renderQuestionsUploadConfirmation = (totalNumberOfQuestions: number) => {
    return (
      <InlineMessage
        title={t('form-sign-up-swipe-config-upload-questions-confirmation-title')}
        body={t('form-sign-up-swipe-config-upload-questions-confirmation-body', {
          numberOfQuestions: totalNumberOfQuestions,
        })}
        variant="positive"
      />
    );
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        const formatErrors = error && Array.isArray(error);
        return (
          <>
            <FormField
              shouldValidate={shouldValidate(error)}
              valid={!error}
              validation={{
                id: `controlled-input-swipe-questions--${name}`,
                msg: transformErrorMessage(error),
                type: 'error',
              }}
              fieldHelper={fieldHelper}
            >
              <FormFieldChildren
                render={(describedById) => (
                  <div className={styles['upload-questions-button-container']}>
                    <UploadSwipeQuestions
                      handleQuestions={(questions, filename) => {
                        onChange(questions);
                        setQuestionsFileName(filename);
                      }}
                      handleError={handleError}
                    />
                    {!formatErrors &&
                      questions &&
                      renderQuestionsUploadConfirmation(questions.length)}
                    <DownloadSwipeQuestions
                      questions={swipeQuestionsTemplateData[language]}
                      label={t('general-template').toLowerCase() + '.xlsx'}
                      fileName={t('general-template').toLowerCase() + '.xlsx'}
                    />

                    {value && (
                      <DownloadSwipeQuestions
                        questions={value}
                        label={questionsFileName}
                        fileName={questionsFileName}
                      />
                    )}
                  </div>
                )}
              />
            </FormField>
            {formatErrors && (
              <div className={styles['form-elements']}>
                <UploadQuestionsErrorTheatre
                  open={showErrors}
                  error={error}
                  limits={limits}
                  onClose={() => setShowErrors(false)}
                />
                <Button
                  text={t('swipe-upload-questions-show-format-errors-button-text')}
                  type="danger"
                  onClick={() => setShowErrors(true)}
                />
              </div>
            )}
          </>
        );
      }}
    />
  );
};
